import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { designWeights } from "services/constants/designTokens";
import { dashboard } from "./dashboardStyles";

// Quick Actions row — shortcuts that jump straight to the relevant My Zone >
// Apply tab (the parent reads ?view= and selects the tab). Every target is an
// existing Apply section, so nothing here needs its own backend.
export const QuickActionsSection = () => {
  const history = useHistory();
  const { userScreenAccess } = useSelector((state) => state.tokenReducer);

  // Each shortcut opens straight into a specific My Request submodule's
  // create form -- gated the same "hide, don't show a dead shortcut" way as
  // the actual create button on each destination screen, using the exact
  // submodule name that screen's own check already uses:
  //  - Apply Leave -> Leave/index.js's own "Leaves"
  //  - Apply Permission -> WorkAdjustment's "Permission" tab (view=work-adjustment
  //    with no &tab= defaults to the first tab, "permission")
  //  - Apply Overtime -> Overtime/index.js's own "Myzone Overtime"
  //  - Attendance Correction -> AttendanceCorrection/index.js's own "Attendance Correction"
  //  - Add Expense -> `view=expense` opens the Expense Claims tab of the
  //    Finance Requests screen (MyZoneApply/index.js), gated on
  //    ExpenseClaim.js's own "Expense Claims" submodule -- previously this
  //    linked to `view=advance-salary`, which actually opens the Salary
  //    Advance tab instead, despite the "Add Expense" label.
  const canApplyLeave = checkEditorPermission(
    userScreenAccess?.module_details,
    "My Request",
    "Leaves",
  );
  const canApplyPermission = checkEditorPermission(
    userScreenAccess?.module_details,
    "My Request",
    "Permission",
  );
  const canApplyOvertime = checkEditorPermission(
    userScreenAccess?.module_details,
    "My Request",
    "Myzone Overtime",
  );
  const canApplyAttendanceCorrection = checkEditorPermission(
    userScreenAccess?.module_details,
    "My Request",
    "Attendance Correction",
  );
  const canApplyExpense = checkEditorPermission(
    userScreenAccess?.module_details,
    "My Request",
    "Expense Claims",
  );

  const actions = [
    { label: "Apply Leave", icon: CalendarMonthIcon, color: dashboard.colors.green, to: "/admin/myzone/apply?view=leave&apply=true", show: canApplyLeave },
    { label: "Apply Permission", icon: PersonAddAltIcon, color: dashboard.colors.amber, to: "/admin/myzone/apply?view=work-adjustment", show: canApplyPermission },
    { label: "Apply Overtime", icon: AccessTimeIcon, color: dashboard.colors.purple, to: "/admin/myzone/apply?view=overtime", show: canApplyOvertime },
    { label: "Attendance Correction", icon: EditCalendarIcon, color: dashboard.colors.blue, to: "/admin/myzone/apply?view=attendance-correction", show: canApplyAttendanceCorrection },
    { label: "Add Expense", icon: AccountBalanceWalletIcon, color: "#15B79E", to: "/admin/myzone/apply?view=expense", show: canApplyExpense },
  ].filter((a) => a.show);

  if (actions.length === 0) return null;

  return (
    <Box sx={{ ...dashboard.cardSx, mb: 0.5 }}>
      <Typography fontWeight={designWeights.heading} sx={{ color: dashboard.colors.text, mb: 1.5 }}>
        Quick Actions
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ flexWrap: { sm: "wrap" } }}
      >
        {actions.map((a) => {
          const Icon = a.icon;
          return (
            <Box
              key={a.label}
              onClick={() => history.push(a.to)}
              sx={{
                flex: { sm: 1 },
                minWidth: { sm: 150 },
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 2,
                py: 1.25,
                borderRadius: 2,
                border: `1px solid ${dashboard.colors.subText}22`,
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": { borderColor: a.color, bgcolor: `${a.color}0D` },
              }}
            >
              <Box sx={dashboard.iconWrap(a.color)}>
                <Icon fontSize="small" />
              </Box>
              <Typography fontWeight={designWeights.medium} sx={{ color: dashboard.colors.text, fontSize: 14, lineHeight: 1.2 }}>
                {a.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
